import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getInvitationProject,
  getSangjitRsvps,
  createSangjitRsvp,
  updateSangjitRsvp,
  deleteSangjitRsvp
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft,
  Plus, 
  Edit, 
  Trash2,
  Calendar,
  MapPin,
  Users,
  Heart,
  Loader2,
  Search,
  UserCheck,
  UserX,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

export const InvitationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLeader, user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    guest_name: '',
    status: 'No Attendance',
    no_table: '0',
    description: ''
  });
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, rsvpsRes] = await Promise.all([
        getInvitationProject(id),
        getSangjitRsvps(id)
      ]);
      setProject(projectRes.data);
      setRsvps(rsvpsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      navigate('/invitations');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (rsvp = null) => {
    if (rsvp) {
      setEditing(rsvp);
      setForm({
        guest_name: rsvp.guest_name,
        status: rsvp.status,
        no_table: rsvp.no_table.toString(),
        description: rsvp.description || ''
      });
    } else {
      setEditing(null);
      setForm({
        guest_name: '',
        status: 'No Attendance',
        no_table: '0',
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.guest_name) {
      toast.error('Guest name is required');
      return;
    }

    try {
      const data = {
        guest_name: form.guest_name,
        status: form.status,
        no_table: parseInt(form.no_table) || 0,
        description: form.description
      };

      if (editing) {
        await updateSangjitRsvp(editing.id, data);
        toast.success('RSVP updated');
      } else {
        await createSangjitRsvp(id, { ...data, invitation_id: id });
        toast.success('RSVP created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save RSVP');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSangjitRsvp(deleteId);
      toast.success('RSVP deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete RSVP');
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'No Attendance') {
      return 'bg-gray-100 text-gray-700 border-gray-300';
    } else if (status === 'Yes - 1 Person') {
      return 'bg-green-50 text-green-700 border-green-300';
    } else if (status === 'Yes - 2 People') {
      return 'bg-blue-50 text-blue-700 border-blue-300';
    }
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getStatusIcon = (status) => {
    if (status === 'No Attendance') {
      return <UserX className="w-4 h-4" />;
    }
    return <UserCheck className="w-4 h-4" />;
  };

  const filteredRsvps = rsvps.filter(r => 
    r.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalAttending = rsvps.reduce((sum, r) => {
    if (r.status === 'Yes - 1 Person') return sum + 1;
    if (r.status === 'Yes - 2 People') return sum + 2;
    return sum;
  }, 0);
  
  const notAttending = rsvps.filter(r => r.status === 'No Attendance').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div data-testid="invitation-detail-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/invitations')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-imperial flex items-center gap-2">
              <Heart className="w-8 h-8" />
              {project.bride_groom_name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(project.sangjit_date)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {project.sangjit_place}
              </span>
            </div>
          </div>
        </div>
        <Button
          onClick={() => openModal()}
          className="bg-imperial hover:bg-imperial/90"
          data-testid="add-rsvp-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Guest
        </Button>
      </div>

      {/* Project Info Card (Read-only for clients) */}
      <Card className="card-luxury">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Bride & Groom</p>
              <p className="font-medium">{project.bride_groom_name}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Date</p>
              <p className="font-medium">{formatDate(project.sangjit_date)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Place</p>
              <p className="font-medium">{project.sangjit_place}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Target Pax</p>
              <p className="font-heading text-2xl font-bold">{project.target_pax}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Attending</p>
              <p className="font-heading text-2xl font-bold text-green-600">{totalAttending}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-luxury">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-imperial mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Total RSVPs</p>
            <p className="font-heading text-2xl font-bold">{rsvps.length}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4 text-center">
            <UserCheck className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Attending</p>
            <p className="font-heading text-2xl font-bold text-green-600">{totalAttending}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4 text-center">
            <UserX className="w-8 h-8 mx-auto text-gray-500 mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Not Attending</p>
            <p className="font-heading text-2xl font-bold text-gray-500">{notAttending}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4 text-center">
            <Heart className="w-8 h-8 mx-auto text-gold mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Progress</p>
            <p className="font-heading text-2xl font-bold text-gold">
              {project.target_pax > 0 ? Math.round((totalAttending / project.target_pax) * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12 bg-white border-warm-grey"
          data-testid="search-rsvps"
        />
      </div>

      {/* RSVPs Table */}
      <Card className="card-luxury">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Guest List</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Table No.</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Modified By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRsvps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? 'No guests match your search' : 'No RSVPs yet. Add your first guest!'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRsvps.map((rsvp) => (
                    <TableRow key={rsvp.id} data-testid={`rsvp-${rsvp.id}`}>
                      <TableCell className="font-medium">{rsvp.guest_name}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(rsvp.status)} border flex items-center gap-1 w-fit`}>
                          {getStatusIcon(rsvp.status)}
                          {rsvp.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{rsvp.no_table || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate">{rsvp.description || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(rsvp.submitted_at)}
                      </TableCell>
                      <TableCell>
                        {rsvp.modify_by && (
                          <div className="text-sm">
                            <p className="text-muted-foreground">{rsvp.modify_by}</p>
                            <p className="text-xs text-muted-foreground/70">
                              {formatDateTime(rsvp.modify_date)}
                            </p>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openModal(rsvp)}
                            data-testid={`edit-rsvp-${rsvp.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteId(rsvp.id)}
                            data-testid={`delete-rsvp-${rsvp.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? 'Edit RSVP' : 'Add Guest'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Guest Name *
              </Label>
              <Input
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                placeholder="Enter guest name"
                data-testid="guest-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Status *
              </Label>
              <Select 
                value={form.status} 
                onValueChange={(value) => setForm({ ...form, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No Attendance">No Attendance</SelectItem>
                  <SelectItem value="Yes - 1 Person">Yes - 1 Person</SelectItem>
                  <SelectItem value="Yes - 2 People">Yes - 2 People</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Table Number
              </Label>
              <Input
                type="number"
                value={form.no_table}
                onChange={(e) => setForm({ ...form, no_table: e.target.value })}
                placeholder="0"
                data-testid="table-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Description / Notes
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              className="bg-imperial hover:bg-imperial/90"
              data-testid="save-rsvp-button"
            >
              {editing ? 'Update' : 'Add Guest'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete RSVP?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently remove this guest from the list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-rsvp"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvitationDetailPage;
